import explorerData from './tase-index-constituents.generated.json';

const root = document.querySelector('[data-tase-index-explorer]');

if (root) {
  bootstrapTaseExplorer(root);
}

function bootstrapTaseExplorer(rootNode) {
  const indexById = new Map(explorerData.indices.map((index) => [index.id, index]));
  const overlapByPair = new Map(
    explorerData.overlap.map((item) => [`${item.leftIndexId}::${item.rightIndexId}`, item])
  );
  const query = new URLSearchParams(window.location.search);
  const defaultIndexId = explorerData.indices[0].id;
  const state = {
    indexId: indexById.has(query.get('index')) ? query.get('index') : defaultIndexId,
    compareIndexId: indexById.has(query.get('compare')) ? query.get('compare') : 'ta-125',
    search: query.get('q') || '',
    theme: query.get('theme') || '',
    minWeight: Number(query.get('minWeight') || 0),
    profileOnly: query.get('profiles') === '1',
    sortKey: query.get('sort') || 'weight',
    sortDirection: query.get('dir') === 'asc' ? 'asc' : 'desc',
  };
  if (state.compareIndexId === state.indexId) {
    state.compareIndexId = explorerData.indices.find((index) => index.id !== state.indexId)?.id || '';
  }

  const elements = {
    indexSelect: rootNode.querySelector('[data-tase-index-select]'),
    search: rootNode.querySelector('[data-tase-search]'),
    theme: rootNode.querySelector('[data-tase-theme-select]'),
    minWeight: rootNode.querySelector('[data-tase-weight-select]'),
    profileOnly: rootNode.querySelector('[data-tase-profile-only]'),
    compareSelect: rootNode.querySelector('[data-tase-compare-select]'),
    status: rootNode.querySelector('[data-tase-status]'),
    activeFilters: rootNode.querySelector('[data-tase-active-filters]'),
    reset: rootNode.querySelector('[data-tase-reset]'),
    brief: rootNode.querySelector('[data-tase-brief]'),
    kpis: rootNode.querySelector('[data-tase-kpis]'),
    sectors: rootNode.querySelector('[data-tase-sector-breakdown]'),
    themes: rootNode.querySelector('[data-tase-theme-breakdown]'),
    overlap: rootNode.querySelector('[data-tase-overlap]'),
    table: rootNode.querySelector('[data-tase-table]'),
    method: rootNode.querySelector('[data-tase-method]'),
    sources: rootNode.querySelector('[data-tase-sources]'),
    copyLink: rootNode.querySelector('[data-copy-tase-link]'),
    exportCsv: rootNode.querySelector('[data-export-tase-csv]'),
    print: rootNode.querySelector('[data-print-tase]'),
  };

  elements.indexSelect.addEventListener('change', () => {
    state.indexId = elements.indexSelect.value;
    if (state.compareIndexId === state.indexId) {
      state.compareIndexId = explorerData.indices.find((index) => index.id !== state.indexId)?.id || '';
    }
    render();
  });
  elements.compareSelect.addEventListener('change', () => {
    state.compareIndexId = elements.compareSelect.value;
    render();
  });
  elements.search.addEventListener('input', () => {
    state.search = elements.search.value.trim();
    render();
  });
  elements.theme.addEventListener('change', () => {
    state.theme = elements.theme.value;
    render();
  });
  elements.minWeight.addEventListener('change', () => {
    state.minWeight = Number(elements.minWeight.value || 0);
    render();
  });
  elements.profileOnly.addEventListener('change', () => {
    state.profileOnly = elements.profileOnly.checked;
    render();
  });
  elements.table.addEventListener('click', (event) => {
    const sortButton = event.target.closest('[data-tase-sort]');
    if (!sortButton) {
      return;
    }
    const nextKey = sortButton.dataset.taseSort;
    if (state.sortKey === nextKey) {
      state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      state.sortKey = nextKey;
      state.sortDirection = nextKey === 'name' ? 'asc' : 'desc';
    }
    render();
  });
  elements.copyLink.addEventListener('click', async () => {
    const original = elements.copyLink.textContent;
    try {
      await copyText(window.location.href);
      elements.copyLink.textContent = 'Link copied';
    } catch {
      elements.copyLink.textContent = 'Copy failed';
    }
    window.setTimeout(() => {
      elements.copyLink.textContent = original;
    }, 1600);
  });
  elements.exportCsv.addEventListener('click', () => {
    const rows = getVisibleRows(state);
    downloadCsv(rows, state.indexId);
  });
  elements.print.addEventListener('click', () => window.print());
  elements.reset.addEventListener('click', () => {
    state.indexId = defaultIndexId;
    state.compareIndexId = explorerData.indices.find((index) => index.id !== defaultIndexId)?.id || '';
    state.search = '';
    state.theme = '';
    state.minWeight = 0;
    state.profileOnly = false;
    state.sortKey = 'weight';
    state.sortDirection = 'desc';
    render();
  });
  elements.themes.addEventListener('click', (event) => {
    const bar = event.target.closest('[data-theme-jump]');
    if (!bar || !bar.dataset.themeJump) {
      return;
    }
    state.theme = state.theme === bar.dataset.themeJump ? '' : bar.dataset.themeJump;
    render();
  });
  elements.activeFilters.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-tase-clear]');
    if (!chip) {
      return;
    }
    const key = chip.dataset.taseClear;
    if (key === 'search') state.search = '';
    if (key === 'theme') state.theme = '';
    if (key === 'minWeight') state.minWeight = 0;
    if (key === 'profileOnly') state.profileOnly = false;
    render();
  });

  function render() {
    const index = indexById.get(state.indexId) || explorerData.indices[0];
    const compareIndex = indexById.get(state.compareIndexId);
    const rows = getVisibleRows(state);

    elements.indexSelect.value = state.indexId;
    elements.search.value = state.search;
    elements.theme.value = state.theme;
    elements.minWeight.value = String(state.minWeight);
    elements.profileOnly.checked = state.profileOnly;
    elements.compareSelect.value = state.compareIndexId;

    updateUrl(state);
    renderActiveFilters(state, elements.activeFilters);
    renderBrief(index, elements.brief);
    renderKpis(index, rows, elements.kpis);
    renderBars(index.sectors, elements.sectors, 'sector');
    renderBars(index.themes, elements.themes, 'theme', state.theme);
    renderOverlap(index, compareIndex, elements.overlap);
    renderTable(rows, elements.table, state);
    renderMethod(index, elements.method);
    renderSources(elements.sources);

    elements.status.textContent = `Showing ${rows.length} of ${index.constituentCount} tracked ${index.name} rows.`;
  }

  render();
}

function getVisibleRows(state) {
  const query = state.search.toLowerCase();
  const rows = explorerData.constituents
    .map((constituent) => ({
      ...constituent,
      weightPct: membershipWeight(constituent, state.indexId),
      membership: constituent.memberships.find((item) => item.indexId === state.indexId),
    }))
    .filter((constituent) => constituent.weightPct > 0)
    .filter((constituent) => !state.minWeight || constituent.weightPct >= state.minWeight)
    .filter((constituent) => !state.profileOnly || constituent.profilePath)
    .filter((constituent) => !state.theme || constituent.strategicThemes.includes(state.theme))
    .filter((constituent) => {
      if (!query) {
        return true;
      }
      return [
        constituent.name,
        constituent.ticker,
        constituent.isin,
        constituent.sector,
        constituent.industry,
        constituent.marketRole,
        ...constituent.strategicThemeLabels.map((theme) => theme.label),
      ].join(' ').toLowerCase().includes(query);
    });

  return sortRows(rows, state.sortKey, state.sortDirection);
}

function sortRows(rows, key, direction) {
  const multiplier = direction === 'asc' ? 1 : -1;
  const read = (row) => {
    if (key === 'name') return row.name;
    if (key === 'ticker') return row.ticker;
    if (key === 'sector') return row.sector;
    if (key === 'profile') return row.profilePath ? 1 : 0;
    return row.weightPct;
  };
  return [...rows].sort((a, b) => {
    const left = read(a);
    const right = read(b);
    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * multiplier || a.name.localeCompare(b.name);
    }
    return String(left).localeCompare(String(right)) * multiplier || b.weightPct - a.weightPct;
  });
}

function renderBrief(index, container) {
  const partial = index.expectedConstituentCount > 0 && index.trackedCoveragePct < 100;
  const coverageNote = index.expectedConstituentCount > 0
    ? `Tracking ${formatNumber(index.constituentCount)} of ${formatNumber(index.expectedConstituentCount)} official constituents (${index.trackedCoveragePct}% coverage).`
    : `Curated research lens tracking ${formatNumber(index.constituentCount)} constituents.`;

  container.innerHTML = `
    <div class="tase-brief__heading">
      <div>
        <span class="tase-eyebrow">${escapeHtml(index.family)} · ${escapeHtml(index.coverage)}</span>
        <h2 id="tase-result-title">${escapeHtml(index.name)}</h2>
      </div>
      <a class="tase-source-chip" href="${escapeHtml(index.sourceUrl)}" target="_blank" rel="noopener noreferrer">Open TASE source</a>
    </div>
    <p class="tase-coverage${partial ? ' tase-coverage--partial' : ''}">${escapeHtml(coverageNote)}</p>
    <p>${escapeHtml(index.description)}</p>
    <div class="tase-warning">
      <strong>Read as research context.</strong>
      <span>${escapeHtml(explorerData.meta.liveDataWarning)}</span>
    </div>
  `;
}

function renderKpis(index, visibleRows, container) {
  const visibleWeight = visibleRows.reduce((sum, row) => sum + row.weightPct, 0);
  const kpis = [
    ['Tracked rows', formatNumber(index.constituentCount)],
    ['Top 10 weight', `${formatPercent(index.top10WeightPct)}`],
    ['Visible weight (of tracked)', `${formatPercent(visibleWeight)}`],
    ['HHI score', formatNumber(index.hhi)],
    ['Profile links', formatNumber(index.profileLinkCount)],
    ['Dual-listed rows', formatNumber(index.dualListedCount)],
  ];

  container.innerHTML = kpis.map(([label, value]) => `
    <article class="tase-kpi">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
    </article>
  `).join('');
}

function renderBars(items, container, kind, activeTheme = '') {
  const rows = (items || []).slice(0, 10);
  if (!rows.length) {
    container.innerHTML = '<p class="tase-empty">No concentration data available for this lens.</p>';
    return;
  }

  const renderRowContent = (item) => `
    <span class="tase-bar-row__label">${escapeHtml(item.label)}</span>
    <span class="tase-bar-track" aria-hidden="true"><i style="width:${Math.max(item.weightPct, 2)}%"></i></span>
    <strong>${formatPercent(item.weightPct)}</strong>
  `;

  container.innerHTML = `
    <div class="tase-bar-list">
      ${rows.map((item) => {
        if (kind !== 'theme') {
          return `<div class="tase-bar-row">${renderRowContent(item)}</div>`;
        }
        const themeId = themeIdForLabel(item.label);
        const active = Boolean(themeId) && themeId === activeTheme;
        return `
          <button class="tase-bar-row tase-bar-row--action${active ? ' is-active' : ''}" type="button" data-theme-jump="${escapeHtml(themeId)}" aria-pressed="${active ? 'true' : 'false'}" title="Filter the table by this theme">
            ${renderRowContent(item)}
          </button>
        `;
      }).join('')}
    </div>
  `;
}

function renderActiveFilters(state, container) {
  const chips = [];
  if (state.search) {
    chips.push(['search', `Search: ${state.search}`]);
  }
  if (state.theme) {
    const theme = explorerData.themes.find((item) => item.id === state.theme);
    chips.push(['theme', `Theme: ${theme?.label || state.theme}`]);
  }
  if (state.minWeight) {
    chips.push(['minWeight', `Weight ≥ ${state.minWeight}%`]);
  }
  if (state.profileOnly) {
    chips.push(['profileOnly', 'Profiles only']);
  }

  container.hidden = !chips.length;
  container.innerHTML = chips.map(([key, label]) => `
    <button class="tase-filter-chip" type="button" data-tase-clear="${escapeHtml(key)}" title="Clear this filter">
      ${escapeHtml(label)} <span aria-hidden="true">×</span>
    </button>
  `).join('');
}

function renderOverlap(index, compareIndex, container) {
  if (!compareIndex || compareIndex.id === index.id) {
    container.innerHTML = '<p class="tase-empty">Choose a different comparison lens to inspect overlap.</p>';
    return;
  }

  const overlap = explorerData.overlap.find(
    (item) => item.leftIndexId === index.id && item.rightIndexId === compareIndex.id
  );
  if (!overlap) {
    container.innerHTML = '<p class="tase-empty">No overlap record is available for this pair.</p>';
    return;
  }

  container.innerHTML = `
    <div class="tase-overlap-summary">
      <article>
        <span>Shared rows</span>
        <strong>${formatNumber(overlap.sharedCount)}</strong>
      </article>
      <article>
        <span>${escapeHtml(index.name)} shared weight</span>
        <strong>${formatPercent(overlap.leftSharedWeightPct)}</strong>
      </article>
      <article>
        <span>${escapeHtml(compareIndex.name)} shared weight</span>
        <strong>${formatPercent(overlap.rightSharedWeightPct)}</strong>
      </article>
    </div>
    <div class="tase-overlap-grid">
      ${renderOverlapList('Shared constituents', overlap.shared, true)}
      ${renderOverlapList(`${index.name} only`, overlap.leftOnly)}
      ${renderOverlapList(`${compareIndex.name} only`, overlap.rightOnly)}
    </div>
  `;
}

function renderOverlapList(title, rows, showBothWeights = false) {
  if (!rows.length) {
    return `
      <section class="tase-overlap-list">
        <h3>${escapeHtml(title)}</h3>
        <p class="tase-empty">No rows in this bucket.</p>
      </section>
    `;
  }

  return `
    <section class="tase-overlap-list">
      <h3>${escapeHtml(title)}</h3>
      <ol>
        ${rows.map((row) => `
          <li>
            <span><strong>${escapeHtml(row.ticker)}</strong> ${escapeHtml(row.name)}</span>
            <small>${showBothWeights
              ? `${formatPercent(row.leftWeightPct)} / ${formatPercent(row.rightWeightPct)}`
              : formatPercent(row.weightPct)}
            </small>
          </li>
        `).join('')}
      </ol>
    </section>
  `;
}

function renderTable(rows, container, state) {
  if (!rows.length) {
    container.innerHTML = '<p class="tase-empty">No constituents match the current filters.</p>';
    return;
  }

  container.innerHTML = `
    <table class="tase-table">
      <thead>
        <tr>
          ${renderSortHeader('Company', 'name', state)}
          ${renderSortHeader('Ticker', 'ticker', state)}
          ${renderSortHeader('Weight', 'weight', state)}
          ${renderSortHeader('Sector', 'sector', state)}
          <th scope="col">Themes</th>
          ${renderSortHeader('Profile', 'profile', state)}
        </tr>
      </thead>
      <tbody>
        ${rows.map((row) => `
          <tr>
            <th scope="row">
              <span class="tase-company-name">${escapeHtml(row.name)}</span>
              <small>${escapeHtml(row.marketRole || row.industry)}</small>
            </th>
            <td><code>${escapeHtml(row.ticker)}</code></td>
            <td>
              <strong>${formatPercent(row.weightPct)}</strong>
              <small class="tase-weight-source">${row.membership?.source?.startsWith('derived') ? 'derived' : 'curated'}</small>
            </td>
            <td>${escapeHtml(row.sector)}<small>${escapeHtml(row.industry)}</small></td>
            <td>${renderThemeChips(row.strategicThemeLabels)}</td>
            <td>${row.profilePath
              ? `<a class="tase-profile-link" href="${escapeHtml(row.profilePath)}">Open profile</a>`
              : '<span class="tase-muted">No profile</span>'}
            </td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

function renderSortHeader(label, key, state) {
  const active = state.sortKey === key;
  const direction = active ? state.sortDirection : '';
  const marker = active ? (direction === 'asc' ? 'up' : 'down') : 'sort';
  return `
    <th scope="col">
      <button type="button" data-tase-sort="${escapeHtml(key)}">
        ${escapeHtml(label)}
        <span aria-hidden="true">${escapeHtml(marker)}</span>
      </button>
    </th>
  `;
}

function renderThemeChips(themes) {
  return `
    <span class="tase-theme-chips">
      ${themes.slice(0, 4).map((theme) => `<span>${escapeHtml(theme.label)}</span>`).join('')}
    </span>
  `;
}

function renderMethod(index, container) {
  container.innerHTML = `
    <div class="tase-method-copy">
      <p>${escapeHtml(index.methodologyNote)}</p>
      <p>${escapeHtml(explorerData.methodology.weightPolicy)}</p>
      <p>${escapeHtml(explorerData.methodology.profileLinkPolicy)}</p>
      <p>${escapeHtml(explorerData.methodology.apiPolicy)}</p>
    </div>
  `;
}

function renderSources(container) {
  container.innerHTML = `
    <ol class="tase-source-list">
      ${explorerData.sources.map((source) => `
        <li>
          <a href="${escapeHtml(source.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(source.title)}</a>
          <span>${escapeHtml(source.publisher)}</span>
          <small>${escapeHtml(source.note)}</small>
        </li>
      `).join('')}
    </ol>
  `;
}

function membershipWeight(constituent, indexId) {
  const membership = constituent.memberships.find((item) => item.indexId === indexId);
  return Number(membership?.weightPct || 0);
}

function updateUrl(state) {
  const url = new URL(window.location.href);
  url.searchParams.set('index', state.indexId);
  if (state.compareIndexId) url.searchParams.set('compare', state.compareIndexId);
  state.search ? url.searchParams.set('q', state.search) : url.searchParams.delete('q');
  state.theme ? url.searchParams.set('theme', state.theme) : url.searchParams.delete('theme');
  state.minWeight ? url.searchParams.set('minWeight', String(state.minWeight)) : url.searchParams.delete('minWeight');
  state.profileOnly ? url.searchParams.set('profiles', '1') : url.searchParams.delete('profiles');
  url.searchParams.set('sort', state.sortKey);
  url.searchParams.set('dir', state.sortDirection);
  window.history.replaceState({}, '', url);
}

function downloadCsv(rows, indexId) {
  const header = ['index', 'name', 'ticker', 'isin', 'weight_pct', 'sector', 'industry', 'themes', 'profile_path'];
  const lines = [
    header.join(','),
    ...rows.map((row) => [
      indexId,
      row.name,
      row.ticker,
      row.isin,
      row.weightPct,
      row.sector,
      row.industry,
      row.strategicThemeLabels.map((theme) => theme.label).join('|'),
      row.profilePath,
    ].map(csvCell).join(',')),
  ];
  const blob = new Blob([`${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `tase-${indexId}-constituents.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement('textarea');
  input.value = value;
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  input.remove();
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}

function formatPercent(value) {
  return `${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: Number(value) < 10 ? 1 : 0,
  }).format(Number(value || 0))}%`;
}

function themeIdForLabel(label) {
  const match = explorerData.themes.find((theme) => theme.label === label);
  return match?.id || '';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
